import React from 'react'
import { useTranslation } from 'react-i18next'

import MuiLink from '@mui/material/Link'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import LoadingButton from '@mui/lab/LoadingButton'
import PublicIcon from '@mui/icons-material/Public'
import LinkIcon from '@mui/icons-material/Link'
import LockIcon from '@mui/icons-material/Lock'

import { Profile } from '../../types/profile'
import { put, remove } from '../../utils/service'
import Alert from '../alert/alert'
import { generateShareUrl, isSharedProfile, isPublicShareEnabled } from '../../utils/profileEditorUtils'

interface ShareProfileDialogProps {
  currentCustomerUrl: string
  profile: Profile
  onClose: () => void
  onShareLinkCreated: (shareUuid: string) => void
  onAddedToLibrary: (publicShareUuid: string) => void
  onRemovedFromLibrary: () => void
}

const ShareProfileDialog = (props: ShareProfileDialogProps): JSX.Element => {
  const { t } = useTranslation()

  const [isSavingLink, setIsSavingLink] = React.useState<boolean>(false)
  const [isSavingLibrary, setIsSavingLibrary] = React.useState<boolean>(false)
  const [hasSaveLinkError, setHasSaveLinkError] = React.useState<boolean>(false)
  const [hasSaveLibraryError, setHasSaveLibraryError] = React.useState<boolean>(false)
  const [hasRemoveLibraryError, setHasRemoveLibraryError] = React.useState<boolean>(false)
  const [isCopiedToClipboard, setIsCopiedToClipboard] = React.useState<boolean>(false)

  const generateShareLink = (): void => {
    setIsSavingLink(true)
    put<{}, Profile>(`/profiles/${props.profile.id}/enable_share/`, {})
      .then((profileResponse) => {
        setIsSavingLink(false)
        props.onShareLinkCreated(profileResponse.data.share_uuid || '')
      })
      .catch(() => {
        setIsSavingLink(false)
        setHasSaveLinkError(true)
      })
  }

  const removeFromProfileLibrary = (): void => {
    setIsSavingLibrary(true)
    remove<{}>(`/profiles/${props.profile.id}/remove_public_share/`)
      .then(() => {
        setIsSavingLibrary(false)
        props.onRemovedFromLibrary()
      })
      .catch(() => {
        setIsSavingLibrary(false)
        setHasRemoveLibraryError(true)
      })
  }

  const addToProfileLibrary = (): void => {
    setIsSavingLibrary(true)
    put<{}, Profile>(`/profiles/${props.profile.id}/enable_public_share/`, {})
      .then((profileResponse) => {
        setIsSavingLibrary(false)
        props.onAddedToLibrary(profileResponse.data.public_share_uuid || '')
      })
      .catch(() => {
        setIsSavingLibrary(false)
        setHasSaveLibraryError(true)
      })
  }

  return (
    <Dialog open={true} onClose={props.onClose} fullWidth>
      <DialogTitle>{t('profilesview.enableshare')}</DialogTitle>
      <DialogContent>
        {!isSharedProfile(props.profile) && (
          <Stack direction="row">
            <LockIcon fontSize="small" sx={{ mr: 1 }} />
            {t('profilesview.profilenotshared')}
          </Stack>
        )}
        {isPublicShareEnabled(props.profile) && (
          <Stack direction="row">
            <PublicIcon fontSize="small" sx={{ mr: 1 }} />
            {t('profilesview.profilesharedpublic')}
          </Stack>
        )}
        {props.profile.share_uuid && (
          <Stack direction="row">
            <LinkIcon fontSize="small" sx={{ mr: 1 }} />
            {t('profilesview.profilesharedprivate')}
          </Stack>
        )}
        {props.currentCustomerUrl === props.profile.customer && (
          <>
            <p>{t('profilesview.publicsharehelpext')}</p>
            {props.profile.is_bbp_profile && <p>{t('profilesview.bbpshare')}</p>}
            <p>{t('editprofileview.sharedprofileinfo')}</p>
          </>
        )}
        {!isSharedProfile(props.profile) &&
          (props.profile.selected_filters || []).length === 0 &&
          !props.profile.notes && (
            <Alert severity="warning">
              <Stack spacing={2}>
                <Stack>{t('editprofileview.nometadata')}</Stack>
                <Stack>{t('editprofileview.nometadataextra')}</Stack>
              </Stack>
            </Alert>
          )}
        {props.profile.share_uuid && (
          <p>
            {`${t('profilesview.privatesharelink')}: `}
            <MuiLink href={generateShareUrl(props.profile.share_uuid)}>
              {generateShareUrl(props.profile.share_uuid)}
            </MuiLink>
          </p>
        )}
        {hasSaveLinkError && <Alert severity="error">{t('profilesview.shareprofileerror')}</Alert>}
        {hasSaveLibraryError && <Alert severity="error">{t('profilesview.addpublicshareerror')}</Alert>}
        {hasRemoveLibraryError && <Alert severity="error">{t('profilesview.removepublicshareerror')}</Alert>}
      </DialogContent>
      <DialogActions sx={{ alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
        <Button onClick={props.onClose}>{t('common.close')}</Button>
        {!props.profile.share_uuid && props.currentCustomerUrl === props.profile.customer && (
          <LoadingButton startIcon={<LinkIcon />} variant="outlined" loading={isSavingLink} onClick={generateShareLink}>
            {t('profilesview.generatelink')}
          </LoadingButton>
        )}
        {props.profile.share_uuid && (
          <Button
            variant="outlined"
            onClick={() => {
              navigator.clipboard.writeText(generateShareUrl(props.profile.share_uuid || ''))
              setIsCopiedToClipboard(true)
            }}
          >
            {isCopiedToClipboard ? t('profilesview.copiedtoclipboard') : t('profilesview.copytosharelinkclipboard')}
          </Button>
        )}
        {!isPublicShareEnabled(props.profile) &&
          !props.profile.is_bbp_profile &&
          props.currentCustomerUrl === props.profile.customer && (
            <LoadingButton
              startIcon={<PublicIcon />}
              variant="contained"
              loading={isSavingLibrary}
              onClick={addToProfileLibrary}
            >
              {t('profilesview.addtolibrary')}
            </LoadingButton>
          )}
        {isPublicShareEnabled(props.profile) && props.currentCustomerUrl === props.profile.customer && (
          <LoadingButton variant="outlined" color="error" loading={isSavingLibrary} onClick={removeFromProfileLibrary}>
            {t('profilesview.removefromlibrary')}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ShareProfileDialog
